import { ApplicationView, useApplicationView } from "@hooks/useApplicationView";
import { useMemo } from "react";
import { useEntities } from "@hooks/useEntities";
import { useShowWorkspaceSettings } from "@hooks/useShowWorkspaceSettings";
import { useBillingManagement } from "@hooks/useBillingManagement";
import { useBillingType } from "@hooks/useBillingType";
import { SubscriptionType } from "@common/types/billing";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useCanManageWorkspaceMembers } from "@hooks/permissions/useCanManageWorkspaceMembers";
import { useCanManageEntitySettings } from "@hooks/permissions/canViewEntitySettings";
import { getEntityPaneKey } from "./lib";
import {
    EntitySettingsPages,
    UserSettingsPages,
    WorkspaceSettingsPages,
} from "./constants";

export function useValidSettingsPages(): string[] {
    const applicationView = useApplicationView();
    const entities = useEntities();
    const canManageEntitySettings = useCanManageEntitySettings();
    const showWorkspaceSettings = useShowWorkspaceSettings();
    const {
        directory: canUseDirectory,
        workspaceMemberUi: canUseWorkspaceMembers,
        locks: canUseLocks,
    } = useTypedFlags();
    const canManageWorkspaceMembers = useCanManageWorkspaceMembers();
    const { canManageBilling } = useBillingManagement();
    const billingType = useBillingType();
    const showPlans =
        canManageBilling && billingType === SubscriptionType.FLAT_RATE;

    return useMemo(() => {
        switch (applicationView) {
            case ApplicationView.PERSONAL_VIEW:
                return [...Object.values(UserSettingsPages)];
            case ApplicationView.MAIN: {
                const mainViewSettingsPages: string[] = [
                    ...Object.values(UserSettingsPages),
                ];

                if (canManageEntitySettings) {
                    mainViewSettingsPages.push(
                        ...entities
                            .map((e) =>
                                Object.values(EntitySettingsPages).map((p) =>
                                    getEntityPaneKey(p, e),
                                ),
                            )
                            .flat(),
                    );
                }

                if (!showWorkspaceSettings) {
                    return mainViewSettingsPages;
                }

                mainViewSettingsPages.push(WorkspaceSettingsPages.INTEGRATIONS);

                if (canUseWorkspaceMembers && canManageWorkspaceMembers) {
                    mainViewSettingsPages.push(WorkspaceSettingsPages.MEMBERS);
                }

                if (canUseDirectory) {
                    mainViewSettingsPages.push(
                        WorkspaceSettingsPages.DIRECTORY,
                    );
                }

                if (canUseLocks) {
                    mainViewSettingsPages.push(WorkspaceSettingsPages.LOCKS);
                }

                if (showPlans) {
                    mainViewSettingsPages.push(WorkspaceSettingsPages.PLANS);
                }

                if (canManageBilling) {
                    mainViewSettingsPages.push(WorkspaceSettingsPages.BILLING);
                }

                return mainViewSettingsPages;
            }
            default:
                return [];
        }
    }, [
        applicationView,
        canManageEntitySettings,
        entities,
        canUseDirectory,
        canUseLocks,
        canUseWorkspaceMembers,
        canManageWorkspaceMembers,
        canManageBilling,
        showPlans,
        showWorkspaceSettings,
    ]);
}
